import { Avatar, Box, BoxProps } from '@mui/material';
import { useMemo } from 'react';

export type AvatarWithStatusProps = Pick<BoxProps, 'sx'> & {
    status: 'nothing' | 'looking' | 'open' | 'closed' | 'inactive' | 'mixed';
    src?: string;
    alt?: string;
    size?: 'sm' | 'md' | 'lg' | 'xl';
    /** Called when clicking the status badge only */
    onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
    /** Called when clicking the avatar image. When provided, avatar click is independent from onClick. */
    onAvatarClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
};

const AvatarWithStatus: React.FC<AvatarWithStatusProps> = ({ status, src, alt, size = 'sm', sx, onClick, onAvatarClick }) => {
    const { background, content, borderColor, borderWidth, width, height, infoStyle } = useMemo(() => {
        const getBackground = (status: AvatarWithStatusProps['status']) => {
            switch (status) {
                case 'open':
                    return 'linear-gradient(180deg, #11998E 0%, #38EF7D 100%)';
                case 'closed':
                    return '#4239B0';
                case 'inactive':
                    return '#D34059';
                case 'looking':
                    return 'linear-gradient(93deg, #FDC830 5.15%, #F37335 95.94%)';
                case 'mixed':
                    return '#E9A0AC';
                case 'nothing':
                default:
                    return '#CBCBCB';
            }
        };
        const getBorderColor = (status: AvatarWithStatusProps['status']) => {
            switch (status) {
                case 'open':
                    return '#11998E';
                case 'closed':
                    return '#4239B0';
                case 'inactive':
                    return '#D34059';
                case 'looking':
                    return '#F37335';
                case 'mixed':
                    return '#E9A0AC';
                case 'nothing':
                default:
                    return '#CBCBCB';
            }
        };
        const getContent = (status: AvatarWithStatusProps['status']): React.ReactNode => {
            switch (status) {
                case 'open':
                    return 'Disponible';
                case 'closed':
                    return 'Non disponible';
                case 'inactive':
                    return 'Inactif';
                case 'looking':
                    return 'À l\'écoute';
                case 'mixed':
                    return 'Statuts mixtes';
                case 'nothing':
                default:
                    return 'Non renseigné';
            }
        };
        const getBorderWidth = (size: AvatarWithStatusProps['size']) => {
            switch (size) {
                case 'xl':
                    return '6px';
                case 'lg':
                    return '4px';
                case 'md':
                    return '3px';
                case 'sm':
                default:
                    return '2px';
            }
        };
        const getDimensions: (size: AvatarWithStatusProps['size']) => { width: string; height: string } = size => {
            switch (size) {
                case 'xl':
                    return { width: '140px', height: '140px' };
                case 'lg':
                    return { width: '102px', height: '102px' };
                case 'md':
                    return { width: '50px', height: '50px' };
                case 'sm':
                default:
                    return { width: '42px', height: '42px' };
            }
        };

        const getInfoStyle = (size: AvatarWithStatusProps['size']) => {
            switch (size) {
                case 'xl':
                    return {
                        fontSize: '16px',
                        height: '36px',
                        padding: '0 8px',
                        borderRadius: '16px',
                        minWidth: '110px',
                        marginTop: '-30px',
                    };
                case 'lg':
                    return {
                        fontSize: '12px',
                        height: '26px',
                        padding: '0 9px',
                        borderRadius: '12px',
                        minWidth: '36px',
                        marginTop: '-20px',
                    };
                case 'sm':
                default:
                    return {
                        fontSize: '10px',
                        height: '20px',
                        whiteSpace: 'nowrap',
                        padding: '0 6px',
                        borderRadius: '16px',
                        minWidth: '34px',
                        marginTop: '-8px',
                    };
            }
        };

        const { width, height } = getDimensions(size);
        return {
            background: getBackground(status),
            content: getContent(status),
            borderColor: getBorderColor(status),
            borderWidth: getBorderWidth(size),
            width,
            height,
            infoStyle: getInfoStyle(size),
        };
    }, [status, size]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width,
                cursor: (onClick || onAvatarClick) ? 'pointer' : 'default',
                ...sx,
            }}
        >
            <Avatar
                sx={{
                    width: '100%',
                    height,
                    borderWidth,
                    borderStyle: 'solid',
                    borderColor,
                    cursor: onAvatarClick ? 'pointer' : (onClick ? 'pointer' : 'default'),
                }}
                src={src}
                alt={alt}
                onClick={onAvatarClick
                    ? (e: React.MouseEvent<HTMLDivElement>) => { e.stopPropagation(); onAvatarClick(e); }
                    : onClick
                }
            />
            <Box
                sx={{
                    ...infoStyle,
                    position: 'relative',
                    fontWeight: 700,
                    color: '#FFFFFF',
                    background,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 3,
                    cursor: onClick ? 'pointer' : 'default',
                }}
                onClick={onClick}
            >
                {content}
            </Box>
        </Box>
    );
};

export default AvatarWithStatus;
